import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, RefreshCw, Inbox, LogOut, Search, StickyNote, KeyRound, Trash2, CalendarDays, ChevronLeft, ChevronRight, Plus, X, Download } from "lucide-react";
import { FadeUp, Overline } from "@/components/site/Reveal";
import { inputCls, money, fmtMoney } from "@/components/admin/shared";
import RevenueTab from "@/components/admin/Revenue";
import FollowUpCenter from "@/components/admin/FollowUp";
import NewsAdmin from "@/components/admin/News";
import FairwayTab from "@/components/admin/Fairway";
import FairwayEvents from "@/components/admin/FairwayEvents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  general: "General",
  sponsorship: "Sponsorship",
  partnership: "Partnership",
  guest: "Guest Application",
  event: "Event / Outing",
};

const STATUSES = ["new", "reviewed", "contacted", "closed"];

const STATUS_STYLES = {
  new: "bg-brand-accent text-ink",
  reviewed: "bg-white/15 text-white",
  contacted: "bg-brand text-white",
  closed: "bg-white/5 text-white/40",
};

const TAB_LABELS = {
  submissions: "Submissions",
  fairway: "Fairway Responses",
  "fairway-events": "Fairway Partners",
  followup: "Follow-Ups",
  news: "Golf News",
  calendar: "Calendar",
  episodes: "Episodes",
  shoots: "Shoots",
  pipeline: "Pipeline",
  contacts: "Contacts",
  revenue: "Revenue",
  deals: "Deals",
  deliverables: "Deliverables",
  expenses: "Expenses",
  ideas: "Idea Bank",
  guest_research: "Guest Research",
  courses: "Courses",
};

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("fthoi_admin_token") || "");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [counts, setCounts] = useState({ new: 0, total: 0 });
  const [filters, setFilters] = useState({ q: "", type: "", status: "", date_from: "", date_to: "" });
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [tab, setTab] = useState("submissions");

  const logout = useCallback(() => {
    sessionStorage.removeItem("fthoi_admin_token");
    setToken("");
    setSelected(null);
  }, []);

  const load = useCallback(
    async (f) => {
      if (!token) return;
      setLoading(true);
      try {
        const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
        const headers = { Authorization: `Bearer ${token}` };
        const [subs, cnt] = await Promise.all([
          axios.get(`${API}/admin/submissions`, { headers, params }),
          axios.get(`${API}/admin/submissions/counts`, { headers }),
        ]);
        setSubmissions(subs.data.submissions);
        setCounts(cnt.data);
        setSelected((cur) => (cur ? subs.data.submissions.find((s) => s.id === cur.id) || cur : null));
      } catch (e) {
        if (e?.response?.status === 401) logout();
      } finally {
        setLoading(false);
      }
    },
    [token, logout]
  );

  useEffect(() => {
    const t = setTimeout(() => load(filters), 300);
    return () => clearTimeout(t);
  }, [filters, token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoggingIn(true);
    try {
      const res = await axios.post(`${API}/admin/login`, loginForm);
      sessionStorage.setItem("fthoi_admin_token", res.data.token);
      setToken(res.data.token);
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setLoginError(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setLoggingIn(false);
    }
  };

  const patchSubmission = async (id, payload) => {
    try {
      const res = await axios.patch(`${API}/admin/submissions/${id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelected(res.data);
      setSubmissions((list) => list.map((s) => (s.id === id ? res.data : s)));
      const cnt = await axios.get(`${API}/admin/submissions/counts`, { headers: { Authorization: `Bearer ${token}` } });
      setCounts(cnt.data);
    } catch (e) {
      if (e?.response?.status === 401) logout();
    }
  };

  const setFilter = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  const deleteSubmission = async (id) => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/submissions/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Submission permanently deleted.");
      setSelected(null);
      setConfirmDelete(false);
      await load(filters);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
      } else {
        toast.error("Could not delete the submission. Please try again.");
      }
    } finally {
      setDeleting(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const res = await axios.get(`${API}/admin/submissions/export`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fore-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch (e) {
      if (e?.response?.status === 401) logout();
      else toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const addToPipeline = async () => {
    if (!selected) return;
    try {
      await axios.post(
        `${API}/admin/board/pipeline`,
        {
          data: {
            brand: selected.company || selected.name,
            contact: `${selected.name} — ${selected.email}${selected.phone ? ` · ${selected.phone}` : ""}`,
            stage: "Lead",
            notes: selected.message,
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Added to the sponsorship pipeline.");
    } catch (e) {
      if (e?.response?.status === 401) logout();
      else toast.error("Could not add to pipeline.");
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next.length < 10) {
      toast.error("New password must be at least 10 characters.");
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      toast.error("New passwords don't match.");
      return;
    }
    setPwBusy(true);
    try {
      await axios.post(
        `${API}/admin/change-password`,
        { current_password: pwForm.current, new_password: pwForm.next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Password updated. Use it next time you sign in.");
      setPwForm({ current: "", next: "", confirm: "" });
      setShowPw(false);
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not update password.");
      if (e2?.response?.status === 401 && d !== "Current password is incorrect") logout();
    } finally {
      setPwBusy(false);
    }
  };

  if (!token) {
    return (
      <main className="mx-auto grid min-h-screen max-w-md place-items-center px-5" data-testid="admin-login-page">
        <FadeUp className="w-full">
          <form onSubmit={login} className="w-full border border-white/10 bg-ink-2 p-8 md:p-10" data-testid="admin-login-form">
            <Overline>Fore The Hell Of It — Internal</Overline>
            <h1 className="mt-4 font-display text-3xl font-extrabold uppercase tracking-tight" data-testid="admin-login-heading">
              Admin Login
            </h1>
            <div className="mt-8 flex flex-col gap-5">
              <div>
                <label htmlFor="admin-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-muted">Email</label>
                <input id="admin-email" data-testid="admin-email-input" type="email" required value={loginForm.email} onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))} placeholder="admin email" className={`${inputCls} w-full`} />
              </div>
              <div>
                <label htmlFor="admin-password" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-muted">Password</label>
                <input id="admin-password" data-testid="admin-password-input" type="password" required value={loginForm.password} onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))} placeholder="••••••••••••" className={`${inputCls} w-full`} />
              </div>
            </div>
            {loginError && <p className="mt-4 text-sm text-red-400" data-testid="admin-login-error">{loginError}</p>}
            <button
              type="submit"
              disabled={loggingIn}
              data-testid="admin-login-submit"
              className="mt-8 flex w-full items-center justify-center gap-3 bg-white px-7 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-brand-accent disabled:opacity-60"
            >
              <Lock className="h-4 w-4" />
              {loggingIn ? "Signing In…" : "Sign In"}
            </button>
          </form>
        </FadeUp>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-7xl px-5 pb-24 pt-28 md:px-8 md:pt-32" data-testid="admin-dashboard">
      <FadeUp>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <Overline>Fore The Hell Of It — Internal</Overline>
            <div className="mt-2 flex items-center gap-4">
              <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl" data-testid="admin-dashboard-heading">
                {TAB_LABELS[tab] || "Submissions"}
              </h1>
              {tab === "submissions" && (
                <span className="grid h-8 min-w-8 place-items-center bg-brand-accent px-2 font-mono text-xs font-bold text-ink" data-testid="admin-new-count" title="New submissions">
                  {counts.new} new
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowPw((v) => !v)} data-testid="admin-password-toggle" className={`flex items-center gap-2 border px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${showPw ? "border-brand-accent text-brand-accent" : "border-white/15 text-muted hover:border-brand-accent hover:text-brand-accent"}`}>
              <KeyRound className="h-3.5 w-3.5" />
              Password
            </button>
            <button onClick={() => load(filters)} data-testid="admin-refresh" className="flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-muted transition-colors duration-300 hover:border-brand-accent hover:text-brand-accent">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-muted transition-colors duration-300 hover:border-red-400 hover:text-red-400">
              <LogOut className="h-3.5 w-3.5" />
              Log Out
            </button>
          </div>
        </div>
      </FadeUp>

      <div data-lenis-prevent className="mt-8 flex gap-2 overflow-x-auto whitespace-nowrap border-b border-white/10 pb-px overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] lg:flex-wrap lg:overflow-x-visible lg:whitespace-normal [&::-webkit-scrollbar]:hidden" data-testid="admin-tabs">
        {[
          { id: "submissions", label: "Submissions", icon: Inbox },
          { id: "fairway", label: "Fairway Responses", icon: null },
          { id: "fairway-events", label: "Fairway Partners", icon: null },
          { id: "news", label: "News", icon: null },
          { id: "followup", label: "Follow-Ups", icon: null },
          { id: "calendar", label: "Calendar", icon: CalendarDays },
          { id: "episodes", label: "Episodes", icon: null },
          { id: "shoots", label: "Shoots", icon: null },
          { id: "pipeline", label: "Pipeline", icon: null },
          { id: "contacts", label: "Contacts", icon: null },
          { id: "revenue", label: "Revenue", icon: null },
          { id: "deals", label: "Deals", icon: null },
          { id: "deliverables", label: "Deliverables", icon: null },
          { id: "expenses", label: "Expenses", icon: null },
          { id: "ideas", label: "Ideas", icon: null },
          { id: "guest_research", label: "Guest Research", icon: null },
          { id: "courses", label: "Courses", icon: null },
          { id: "spotlight", label: "Spotlight", icon: null },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            data-testid={`admin-tab-${id}`}
            className={`flex shrink-0 items-center gap-2 border-b-2 px-5 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${
              tab === id ? "border-brand-accent text-white" : "border-transparent text-muted hover:text-white"
            }`}
          >
            {Icon && <Icon className="h-3.5 w-3.5" />}
            {label}
          </button>
        ))}
      </div>

      {showPw && (
        <FadeUp>
          <form onSubmit={changePassword} className="mt-6 border border-white/10 bg-ink-2 p-6 md:p-8" data-testid="admin-password-form">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-accent">Change Admin Password</p>
            <p className="mt-2 text-xs leading-relaxed text-muted">
              Sent over an encrypted connection and stored only as a one-way hash. Minimum 10 characters.
            </p>
            <div className="mt-5 grid gap-4 sm:grid-cols-3">
              <input type="password" required placeholder="Current password" value={pwForm.current} onChange={(e) => setPwForm((f) => ({ ...f, current: e.target.value }))} data-testid="admin-pw-current" className={inputCls} autoComplete="current-password" />
              <input type="password" required minLength={10} placeholder="New password (10+ characters)" value={pwForm.next} onChange={(e) => setPwForm((f) => ({ ...f, next: e.target.value }))} data-testid="admin-pw-new" className={inputCls} autoComplete="new-password" />
              <input type="password" required minLength={10} placeholder="Confirm new password" value={pwForm.confirm} onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))} data-testid="admin-pw-confirm" className={inputCls} autoComplete="new-password" />
            </div>
            <button type="submit" disabled={pwBusy} data-testid="admin-pw-submit" className="mt-5 bg-white px-6 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-brand-accent disabled:opacity-60">
              {pwBusy ? "Updating…" : "Update Password"}
            </button>
          </form>
        </FadeUp>
      )}

      {tab === "submissions" && (<>
      <FadeUp delay={0.1}>
        <div className="mt-8 grid grid-cols-1 gap-3 border border-white/10 bg-ink-2 p-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="admin-filters">
          <div className="relative sm:col-span-2 lg:col-span-1">
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
            <input value={filters.q} onChange={setFilter("q")} placeholder="Search name, email, message…" data-testid="admin-search-input" className={`${inputCls} w-full pl-10`} />
          </div>
          <select value={filters.type} onChange={setFilter("type")} data-testid="admin-filter-type" className={`${inputCls} w-full appearance-none`}>
            <option value="">All Forms</option>
            <option value="general">General</option>
            <option value="sponsorship">Sponsorship</option>
            <option value="partnership">Partnership</option>
            <option value="guest">Guest Application</option>
            <option value="event">Event / Outing</option>
          </select>
          <select value={filters.status} onChange={setFilter("status")} data-testid="admin-filter-status" className={`${inputCls} w-full appearance-none`}>
            <option value="">All Statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
          <input type="date" value={filters.date_from} onChange={setFilter("date_from")} data-testid="admin-filter-date-from" className={`${inputCls} w-full [color-scheme:dark]`} />
          <input type="date" value={filters.date_to} onChange={setFilter("date_to")} data-testid="admin-filter-date-to" className={`${inputCls} w-full [color-scheme:dark]`} />
        </div>
      </FadeUp>

      <div className="mt-4 flex items-center justify-between gap-4">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted" data-testid="admin-results-count">
          {submissions.length} shown{Object.values(filters).some(Boolean) ? " · filters applied" : ""}
        </p>
        <button onClick={exportCsv} disabled={exporting} data-testid="admin-export-csv" className="flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-muted transition-colors duration-300 hover:border-brand-accent hover:text-brand-accent disabled:opacity-60">
          <Download className="h-3.5 w-3.5" />
          {exporting ? "Preparing…" : "Export CSV"}
        </button>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <div className="lg:col-span-2" data-testid="admin-list">
          {submissions.length === 0 ? (
            <div className="grid place-items-center gap-4 border border-white/10 bg-ink-2 py-20 text-center">
              <Inbox className="h-8 w-8 text-white/20" />
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted" data-testid="admin-empty">No submissions match</p>
            </div>
          ) : (
            <div data-lenis-prevent className="admin-scroll flex h-[70vh] min-h-[320px] flex-col gap-2 overflow-y-auto overscroll-contain pr-1">
              {submissions.map((s) => (
                <button
                  key={s.id}
                  onClick={() => { setSelected(s); setNote(""); setConfirmDelete(false); }}
                  data-testid={`admin-row-${s.id}`}
                  className={`w-full border p-4 text-left transition-colors duration-300 ${
                    selected?.id === s.id ? "border-brand-accent bg-ink-2" : "border-white/10 bg-ink-2 hover:border-white/30"
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-display text-base font-extrabold uppercase tracking-tight text-white">{s.name}</span>
                    <span className={`px-2.5 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.2em] ${STATUS_STYLES[s.status || "new"]}`} data-testid={`admin-row-status-${s.id}`}>
                      {s.status || "new"}
                    </span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] text-muted">
                    <span className="text-brand-accent">{TYPE_LABELS[s.inquiry_type] || s.inquiry_type}</span>
                    <span>{s.email}</span>
                  </div>
                  <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white/30">
                    {new Date(s.created_at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-3">
          {!selected ? (
            <div className="grid h-full min-h-[300px] place-items-center border border-dashed border-white/15 text-center" data-testid="admin-detail-empty">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/40">Select a submission to view the full details</p>
            </div>
          ) : (
            <div className="border border-white/10 bg-ink-2 p-7 md:p-9" data-testid="admin-detail">
              <div className="flex flex-wrap items-center gap-3">
                <span className="bg-brand px-3 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.2em] text-white" data-testid="admin-detail-type">
                  {TYPE_LABELS[selected.inquiry_type] || selected.inquiry_type}
                </span>
                <span className="font-mono text-[11px] text-white/40" data-testid="admin-detail-date">
                  {new Date(selected.created_at).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
                </span>
              </div>

              <h2 className="mt-4 font-display text-2xl font-extrabold uppercase tracking-tight" data-testid="admin-detail-name">{selected.name}</h2>

              <div className="mt-4 grid gap-3 border border-white/10 bg-black/30 p-5 font-mono text-xs sm:grid-cols-2" data-testid="admin-detail-fields">
                <div><p className="text-white/35">Email</p><p className="mt-1 text-white">{selected.email}</p></div>
                <div><p className="text-white/35">Phone</p><p className="mt-1 text-white">{selected.phone || "—"}</p></div>
                <div><p className="text-white/35">Business</p><p className="mt-1 text-white">{selected.company || "—"}</p></div>
                <div><p className="text-white/35">Title</p><p className="mt-1 text-white">{selected.title || "—"}</p></div>
                {selected.inquiry_type === "event" && (
                  <>
                    <div><p className="text-white/35">Event Date</p><p className="mt-1 text-white">{selected.event_date || "—"}</p></div>
                    <div><p className="text-white/35">Course / Location</p><p className="mt-1 text-white">{selected.event_location || "—"}</p></div>
                    <div><p className="text-white/35">Event Type</p><p className="mt-1 text-white">{selected.event_type || "—"}</p></div>
                    <div><p className="text-white/35">Expected Attendance</p><p className="mt-1 text-white">{selected.expected_attendance || "—"}</p></div>
                    <div className="sm:col-span-2"><p className="text-white/35">FORE Involvement</p><p className="mt-1 whitespace-pre-wrap text-white">{selected.involvement || "—"}</p></div>
                  </>
                )}
              </div>

              <div className="mt-4 border border-white/10 bg-black/30 p-5" data-testid="admin-detail-message">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/35">Message</p>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-white/80">{selected.message}</p>
              </div>

              <div className="mt-6">
                <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.25em] text-white/35">Set Status</p>
                <div className="flex flex-wrap gap-2" data-testid="admin-status-buttons">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => patchSubmission(selected.id, { status: s })}
                      data-testid={`admin-status-${s}`}
                      className={`px-4 py-2.5 font-mono text-[11px] font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${
                        (selected.status || "new") === s ? STATUS_STYLES[s] : "border border-white/20 text-muted hover:border-white/50 hover:text-white"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {["sponsorship", "partnership"].includes(selected.inquiry_type) && (
                <div className="mt-6">
                  <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.25em] text-white/35">Pipeline</p>
                  <button
                    onClick={addToPipeline}
                    data-testid="admin-add-to-pipeline"
                    className="flex items-center gap-2 border border-brand-accent/50 px-5 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-brand-accent transition-colors duration-300 hover:bg-brand-accent hover:text-ink"
                  >
                    Add to Pipeline
                  </button>
                </div>
              )}

              <div className="mt-8">
                <p className="mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/35">
                  <StickyNote className="h-3.5 w-3.5" /> Internal Notes (private)
                </p>                {(selected.notes || []).length > 0 && (
                  <div className="mb-4 flex flex-col gap-3" data-testid="admin-notes-list">
                    {selected.notes.map((n, i) => (
                      <div key={i} className="border-l-2 border-brand-accent/50 bg-black/30 px-4 py-3">
                        <p className="text-sm text-white/80">{n.text}</p>
                        <p className="mt-1 font-mono text-[10px] text-white/30">
                          {new Date(n.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-3">
                  <input
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Add a private note…"
                    data-testid="admin-note-input"
                    className={`${inputCls} flex-1`}
                  />
                  <button
                    onClick={() => { if (note.trim()) { patchSubmission(selected.id, { note }); setNote(""); } }}
                    data-testid="admin-note-add"
                    className="bg-white px-5 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-brand-accent"
                  >
                    Add
                  </button>
                </div>
              </div>

              <div className="mt-8 border-t border-white/10 pt-6">
                {!confirmDelete ? (
                  <button
                    onClick={() => setConfirmDelete(true)}
                    data-testid="admin-delete-button"
                    className="flex items-center gap-2 border border-red-500/40 px-5 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-red-400 transition-colors duration-300 hover:bg-red-500/10 hover:border-red-400"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete Submission
                  </button>
                ) : (
                  <div className="border border-red-500/40 bg-red-500/10 p-5" data-testid="admin-delete-confirm">
                    <p className="text-sm font-medium text-red-300">
                      Are you sure you want to permanently delete this submission? This cannot be undone.
                    </p>
                    <div className="mt-4 flex flex-wrap gap-3">
                      <button
                        onClick={() => deleteSubmission(selected.id)}
                        disabled={deleting}
                        data-testid="admin-delete-confirm-button"
                        className="bg-red-500 px-5 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-red-400 disabled:opacity-60"
                      >
                        {deleting ? "Deleting…" : "Yes, Delete Permanently"}
                      </button>
                      <button
                        onClick={() => setConfirmDelete(false)}
                        data-testid="admin-delete-cancel"
                        className="border border-white/25 px-5 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-white/60"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      </>)}

      {tab === "calendar" && <TasksCalendar token={token} onAuthFail={logout} />}
      {tab === "fairway" && <FairwayTab token={token} onAuthFail={logout} />}
      {tab === "fairway-events" && <FairwayEvents token={token} onAuthFail={logout} />}
      {tab === "followup" && <FollowUpCenter token={token} onAuthFail={logout} onJump={setTab} />}
      {tab === "news" && <NewsAdmin token={token} onAuthFail={logout} />}
      {tab === "revenue" && <RevenueTab token={token} onAuthFail={logout} />}
      {["episodes", "shoots", "pipeline", "contacts", "deals", "deliverables", "expenses", "ideas", "guest_research", "courses", "spotlight"].includes(tab) && (
        <BoardTab key={tab} kind={tab} token={token} onAuthFail={logout} />
      )}
    </main>
  );
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const EMPTY_TASK = { title: "", date: "", assignee: "", notes: "", done: false };

const TasksCalendar = ({ token, onAuthFail }) => {
  const today = new Date();
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [tasks, setTasks] = useState([]);
  const [shoots, setShoots] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_TASK);
  const [busy, setBusy] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const loadTasks = useCallback(async () => {
    try {
      const [t, s, ep] = await Promise.all([
        axios.get(`${API}/admin/tasks`, { headers }),
        axios.get(`${API}/admin/board/shoots`, { headers }),
        axios.get(`${API}/admin/board/episodes`, { headers }),
      ]);
      setTasks(t.data.tasks);
      setShoots(s.data.items);
      setEpisodes(ep.data.items);
    } catch (e) {
      if (e?.response?.status === 401) onAuthFail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const firstDow = new Date(cursor.y, cursor.m, 1).getDay();
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const dateKey = (d) => `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const tasksFor = (d) => tasks.filter((t) => t.date === dateKey(d));
  const shootsFor = (d) => shoots.filter((s) => s.date === dateKey(d));
  const episodesFor = (d) => episodes.filter((ep) => ep.film_date === dateKey(d));

  const shiftMonth = (delta) => {
    setCursor((c) => {
      const m = c.m + delta;
      return { y: c.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 };
    });
  };

  const openNew = (d) => {
    setForm({ ...EMPTY_TASK, date: d ? dateKey(d) : todayKey });
    setModal({ mode: "new" });
  };

  const openEdit = (t) => {
    setForm({ title: t.title, date: t.date, assignee: t.assignee || "", notes: t.notes || "", done: !!t.done });
    setModal({ mode: "edit", task: t });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      title: form.title,
      date: form.date,
      assignee: form.assignee.trim() || null,
      notes: form.notes.trim() || null,
      done: form.done,
    };
    try {
      if (modal.mode === "new") {
        await axios.post(`${API}/admin/tasks`, payload, { headers });
        toast.success("Action item added.");
      } else {
        await axios.patch(`${API}/admin/tasks/${modal.task.id}`, payload, { headers });
        toast.success("Action item updated.");
      }
      setModal(null);
      await loadTasks();
    } catch (e2) {
      if (e2?.response?.status === 401) onAuthFail();
      else toast.error("Could not save the action item.");
    } finally {
      setBusy(false);
    }
  };

  const removeTask = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/tasks/${modal.task.id}`, { headers });
      toast.success("Action item deleted.");
      setModal(null);
      await loadTasks();
    } catch (e2) {
      if (e2?.response?.status === 401) onAuthFail();
      else toast.error("Could not delete the action item.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8" data-testid="admin-calendar">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button onClick={() => shiftMonth(-1)} data-testid="cal-prev" className="grid h-10 w-10 place-items-center border border-white/15 text-white/70 transition-colors duration-300 hover:border-brand-accent hover:text-brand-accent" aria-label="Previous month">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <h2 className="min-w-56 text-center font-display text-2xl font-extrabold uppercase tracking-tight" data-testid="cal-month-label">
            {MONTHS[cursor.m]} {cursor.y}
          </h2>
          <button onClick={() => shiftMonth(1)} data-testid="cal-next" className="grid h-10 w-10 place-items-center border border-white/15 text-white/70 transition-colors duration-300 hover:border-brand-accent hover:text-brand-accent" aria-label="Next month">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <button onClick={() => openNew(null)} data-testid="cal-new-task" className="flex items-center gap-2 bg-white px-5 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-brand-accent">
          <Plus className="h-3.5 w-3.5" />
          New Action Item
        </button>
      </div>

      <div className="mt-6 grid grid-cols-7 gap-px border border-white/10 bg-white/10">
        {WEEKDAYS.map((w) => (
          <div key={w} className="bg-ink-2 px-2 py-2.5 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-muted">
            {w}
          </div>
        ))}
        {cells.map((d, i) =>
          d === null ? (
            <div key={`blank-${i}`} className="min-h-24 bg-ink md:min-h-28" />
          ) : (
            <div
              key={dateKey(d)}
              onClick={() => openNew(d)}
              data-testid={`cal-day-${dateKey(d)}`}
              className="group min-h-24 cursor-pointer bg-ink p-1.5 transition-colors duration-200 hover:bg-ink-2 md:min-h-28"
            >
              <span className={`grid h-6 w-6 place-items-center font-mono text-[11px] ${dateKey(d) === todayKey ? "bg-brand-accent font-bold text-ink" : "text-white/50"}`}>
                {d}
              </span>
              <div className="mt-1 flex flex-col gap-1">
                {tasksFor(d).map((t) => (
                  <button
                    key={t.id}
                    onClick={(e) => { e.stopPropagation(); openEdit(t); }}
                    data-testid={`cal-task-${t.id}`}
                    className={`border-l-2 px-1.5 py-1 text-left font-mono text-[10px] leading-tight transition-colors duration-200 ${
                      t.done
                        ? "border-white/20 bg-white/5 text-white/35 line-through"
                        : "border-brand-accent bg-brand/20 text-white/85 hover:bg-brand/35"
                    }`}
                  >
                    {t.assignee && <span className="mr-1 text-brand-accent">[{t.assignee}]</span>}
                    {t.title}
                  </button>
                ))}
                {shootsFor(d).map((s) => (
                  <div
                    key={s.id}
                    data-testid={`cal-shoot-${s.id}`}
                    className="border-l-2 border-white/60 bg-white/10 px-1.5 py-1 font-mono text-[10px] leading-tight text-white/70"
                  >
                    <span className="mr-1 text-white/40">[Shoot]</span>
                    {s.title}{s.time ? ` · ${s.time}` : ""}
                  </div>
                ))}
                {episodesFor(d).map((ep) => (
                  <div
                    key={ep.id}
                    data-testid={`cal-episode-${ep.id}`}
                    className="border-l-2 border-brand bg-brand/10 px-1.5 py-1 font-mono text-[10px] leading-tight text-white/70"
                  >
                    <span className="mr-1 text-brand-accent/70">[EP]</span>
                    {ep.title}
                  </div>
                ))}
                <span className="hidden px-1.5 font-mono text-[10px] text-white/25 group-hover:block">+ add</span>
              </div>
            </div>
          )
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-[95] grid place-items-center bg-black/70 p-5 backdrop-blur-sm" data-testid="task-modal" onClick={() => setModal(null)}>
          <form
            onSubmit={save}
            onClick={(e) => e.stopPropagation()}
            data-lenis-prevent
            className="max-h-[85vh] w-full max-w-lg overflow-y-auto overscroll-contain border border-white/15 bg-ink-2 p-7 md:p-9"
            data-testid="task-form"
          >
            <div className="flex items-center justify-between">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand-accent">
                {modal.mode === "new" ? "New Action Item" : "Edit Action Item"}
              </p>
              <button type="button" onClick={() => setModal(null)} className="text-white/50 hover:text-white" aria-label="Close" data-testid="task-modal-close">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-6 flex flex-col gap-4">
              <div>
                <label htmlFor="task-title" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-muted">What needs to be done *</label>
                <input id="task-title" required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Send media kit to Apex Golf" data-testid="task-title-input" className={`${inputCls} w-full`} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="task-assignee" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-muted">Assigned to</label>
                  <input id="task-assignee" value={form.assignee} onChange={(e) => setForm((f) => ({ ...f, assignee: e.target.value }))} placeholder="e.g. Carlos" data-testid="task-assignee-input" className={`${inputCls} w-full`} />
                </div>
                <div>
                  <label htmlFor="task-date" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-muted">Date *</label>
                  <input id="task-date" type="date" required value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="task-date-input" className={`${inputCls} w-full [color-scheme:dark]`} />
                </div>
              </div>
              <div>
                <label htmlFor="task-notes" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-muted">Notes</label>
                <textarea id="task-notes" rows={3} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Details, links, context…" data-testid="task-notes-input" className={`${inputCls} w-full resize-none`} />
              </div>
              {modal.mode === "edit" && (
                <label className="flex cursor-pointer items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-muted">
                  <input type="checkbox" checked={form.done} onChange={(e) => setForm((f) => ({ ...f, done: e.target.checked }))} data-testid="task-done-toggle" className="h-4 w-4 accent-[#CCFF00]" />
                  Mark as done
                </label>
              )}
            </div>

            <div className="mt-7 flex flex-wrap gap-3">
              <button type="submit" disabled={busy} data-testid="task-save" className="bg-brand-accent px-6 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-white disabled:opacity-60">
                {busy ? "Saving…" : modal.mode === "new" ? "Add to Calendar" : "Save Changes"}
              </button>
              {modal.mode === "edit" && (
                <button type="button" onClick={removeTask} disabled={busy} data-testid="task-delete" className="border border-red-500/40 px-6 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-red-400 transition-colors duration-300 hover:bg-red-500/10 disabled:opacity-60">
                  Delete
                </button>
              )}
              <button type="button" onClick={() => setModal(null)} data-testid="task-cancel" className="border border-white/25 px-6 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-white/60">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const BOARD_CONFIGS = {
  episodes: {
    heading: "Episode Ops",
    newLabel: "New Episode",
    titleKey: "title",
    statusKey: "release_status",
    dynamicOptions: { field: "guest", sourceKind: "guest_research", valueKey: "guest" },
    cardRows: [
      { label: "Guest", key: "guest" },
      { label: "Course / Location", key: "location" },
      { label: "Film Date", key: "film_date" },
      { label: "Clips", render: (it) => `${it.clips_completed || 0}/${it.clips_total || 0} completed` },
    ],
    fields: [
      { key: "title", label: "Episode Title *", required: true, placeholder: "e.g. EP. 01 — The First Tee" },
      { key: "guest", label: "Guest (from Guest Research)", placeholder: "Who's on this episode" },
      { key: "location", label: "Course / Location", placeholder: "Where you're filming" },
      { key: "film_date", label: "Filming Date", type: "date" },
      { key: "equipment", label: "Equipment Needed", type: "textarea", placeholder: "Cameras, mics, carts, drones…" },
      { key: "release_status", label: "Release Status", type: "select", options: ["Planning", "Scheduled", "Filmed", "Editing", "Ready", "Published"] },
      { key: "clips_completed", label: "Clips Completed", type: "number" },
      { key: "clips_total", label: "Clips Planned", type: "number" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  shoots: {
    heading: "Shoot Schedule",
    newLabel: "New Shoot",
    titleKey: "title",
    cardRows: [
      { label: "Date", render: (it) => [it.date, it.time].filter(Boolean).join(" · ") },
      { label: "Course / Location", key: "location" },
      { label: "Crew", key: "crew" },
    ],
    fields: [
      { key: "title", label: "Shoot *", required: true, placeholder: "e.g. Tobacco Road vlog" },
      { key: "date", label: "Date", type: "date" },
      { key: "time", label: "Time", placeholder: "e.g. 7:30 AM tee time" },
      { key: "location", label: "Course / Location", placeholder: "Where" },
      { key: "crew", label: "Crew / Who's Needed", placeholder: "e.g. Carlos, Joseph, drone op" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  pipeline: {
    heading: "Sponsorship Pipeline",
    newLabel: "New Lead",
    titleKey: "brand",
    statusKey: "stage",
    cardRows: [
      { label: "Contact", key: "contact" },
      { label: "Value", key: "value" },
      { label: "Next Step", key: "next_step" },
    ],
    fields: [
      { key: "brand", label: "Brand / Partner *", required: true, placeholder: "e.g. Apex Golf Apparel" },
      { key: "contact", label: "Contact Person", placeholder: "Name, email, or phone" },
      { key: "stage", label: "Stage", type: "select", options: ["Lead", "Contacted", "Negotiating", "Signed", "Lost"] },
      { key: "value", label: "Deal Value", placeholder: "e.g. $2,500 / season" },
      { key: "next_step", label: "Next Step Date", type: "date" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  contacts: {
    heading: "Contacts",
    newLabel: "New Contact",
    titleKey: "name",
    statusKey: "role",
    cardRows: [
      { label: "Company / Course", key: "company" },
      { label: "Email", key: "email" },
      { label: "Phone", key: "phone" },
    ],
    fields: [
      { key: "name", label: "Name *", required: true, placeholder: "Full name" },
      { key: "role", label: "Type", type: "select", options: ["Guest", "Brand Rep", "Golf Course", "Other"] },
      { key: "company", label: "Company / Course", placeholder: "Brand, club, or course" },
      { key: "email", label: "Email", type: "text", placeholder: "name@example.com" },
      { key: "phone", label: "Phone", type: "text", placeholder: "(555) 555-5555" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  deals: {
    heading: "Sponsor Deals",
    newLabel: "New Deal",
    titleKey: "sponsor",
    statusKey: "status",
    cardRows: [
      { label: "Value", key: "value" },
      { label: "Contract", render: (it) => [it.contract_start, it.contract_end].filter(Boolean).join(" → ") || null },
      { label: "Payments", key: "payment_schedule" },
      { label: "Renewal", key: "renewal_date" },
    ],
    fields: [
      { key: "sponsor", label: "Sponsor *", required: true, placeholder: "e.g. Apex Golf Apparel" },
      { key: "value", label: "Deal Value", placeholder: "e.g. $5,000 / season" },
      { key: "contract_start", label: "Contract Start", type: "date" },
      { key: "contract_end", label: "Contract End", type: "date" },
      { key: "payment_schedule", label: "Payment Schedule", type: "select", options: ["Upfront", "Monthly", "Quarterly", "50/50", "Net 30", "Custom"] },
      { key: "renewal_date", label: "Renewal Date", type: "date" },
      { key: "status", label: "Status", type: "select", options: ["Active", "Pending", "Renewed", "Ended", "Cancelled"] },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
    totals: (items) => {
      const active = items.filter((i) => i.status === "Active");
      return [
        { label: "Active Deals", value: active.length },
        { label: "Active Value", value: fmtMoney(active.reduce((s, i) => s + money(i.value), 0)) },
      ];
    },
  },
  deliverables: {
    heading: "Sponsor Deliverables",
    newLabel: "New Deliverable",
    titleKey: "deliverable",
    statusKey: "status",
    cardRows: [
      { label: "Sponsor", key: "sponsor" },
      { label: "Type", key: "type" },
      { label: "Due", key: "due_date" },
      { label: "Completed", key: "completed_date" },
    ],
    fields: [
      { key: "sponsor", label: "Sponsor *", required: true, placeholder: "Who this is owed to" },
      { key: "deliverable", label: "Deliverable *", required: true, placeholder: "e.g. 2x Instagram posts + 1 story" },
      { key: "type", label: "Type", type: "select", options: ["Post", "Mention", "Logo Placement", "Episode", "Event Activation", "Other"] },
      { key: "due_date", label: "Due Date", type: "date" },
      { key: "status", label: "Status", type: "select", options: ["Owed", "In Progress", "Delivered"] },
      { key: "completed_date", label: "Completed Date", type: "date" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
    totals: (items) => [
      { label: "Owed", value: items.filter((i) => i.status === "Owed").length },
      { label: "In Progress", value: items.filter((i) => i.status === "In Progress").length },
      { label: "Delivered", value: items.filter((i) => i.status === "Delivered").length },
    ],
  },
  expenses: {
    heading: "Expense Tracker",
    newLabel: "New Expense",
    titleKey: "label",
    statusKey: "category",
    cardRows: [
      { label: "Amount", render: (it) => (it.amount ? fmtMoney(money(it.amount)) : null) },
      { label: "Date", key: "date" },
    ],
    fields: [
      { key: "label", label: "Expense *", required: true, placeholder: "e.g. Drone battery, green fees at Pinehurst" },
      { key: "category", label: "Category", type: "select", options: ["Golf Fees", "Travel", "Equipment", "Editing", "Merchandise", "Advertising", "Events", "Other"] },
      { key: "amount", label: "Amount ($)", type: "number", placeholder: "0.00" },
      { key: "date", label: "Date", type: "date" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
    totals: (items) => {
      const chips = [{ label: "Total Spent", value: fmtMoney(items.reduce((s, i) => s + money(i.amount), 0)) }];
      const byCat = {};
      items.forEach((i) => {
        byCat[i.category || "Other"] = (byCat[i.category || "Other"] || 0) + money(i.amount);
      });
      Object.entries(byCat)
        .sort((a, b) => b[1] - a[1])
        .forEach(([c, v]) => chips.push({ label: c, value: fmtMoney(v) }));
      return chips;
    },
  },
  ideas: {
    heading: "Content Idea Bank",
    newLabel: "New Idea",
    titleKey: "title",
    statusKey: "status",
    cardRows: [
      { label: "Category", key: "category" },
      { label: "Concept", render: (it) => (it.description ? it.description.slice(0, 90) + (it.description.length > 90 ? "…" : "") : null) },
    ],
    fields: [
      { key: "title", label: "Idea Title *", required: true, placeholder: "e.g. 14-club challenge at Tobacco Road" },
      { key: "category", label: "Category", type: "select", options: ["Challenges", "Guests", "Fore Rules", "Course Content", "Sponsor Integrations", "Short Form"] },
      { key: "description", label: "Description", type: "textarea", placeholder: "The concept, hook, format…" },
      { key: "status", label: "Status", type: "select", options: ["Idea", "Planned", "Filmed", "Published"] },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  guest_research: {
    heading: "Guest Research Sheets",
    newLabel: "New Guest Sheet",
    titleKey: "guest",
    statusKey: "status",
    cardRows: [
      { label: "Background", render: (it) => (it.background ? it.background.slice(0, 90) + (it.background.length > 90 ? "…" : "") : null) },
      { label: "Talking Points", render: (it) => (it.talking_points ? it.talking_points.slice(0, 90) + (it.talking_points.length > 90 ? "…" : "") : null) },
    ],
    fields: [
      { key: "guest", label: "Guest Name *", required: true, placeholder: "Who's coming on" },
      { key: "background", label: "Background", type: "textarea", placeholder: "Who they are, their story, handicap, home course…" },
      { key: "talking_points", label: "Talking Points", type: "textarea", placeholder: "Themes and moments to hit on camera" },
      { key: "interview_questions", label: "Interview Questions", type: "textarea", placeholder: "One question per line works well" },
      { key: "avoid", label: "Things to Avoid", type: "textarea", placeholder: "Topics, jokes, or questions to stay away from" },
      { key: "status", label: "Status", type: "select", options: ["Researching", "Ready", "Filmed"] },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  courses: {
    heading: "Course Database",
    newLabel: "New Course",
    titleKey: "name",
    statusKey: "status",
    cardRows: [
      { label: "Location", key: "location" },
      { label: "Cost", key: "cost" },
    ],
    fields: [
      { key: "name", label: "Course Name *", required: true, placeholder: "e.g. Tobacco Road" },
      { key: "location", label: "Location", placeholder: "City, State" },
      { key: "status", label: "Status", type: "select", options: ["Played", "Want to Film", "Contacted", "Filmed"] },
      { key: "cost", label: "Cost", placeholder: "e.g. $85/round, comp'd, TW price" },
      { key: "notes", label: "Notes", type: "textarea", placeholder: "Contact person, signature holes, best time to film…" },
    ],
  },
  spotlight: {
    heading: "Community Spotlight",
    newLabel: "New Spotlight Entry",
    titleKey: "name",
    statusKey: "status",
    cardRows: [
      { label: "Category", key: "category" },
      { label: "Description", render: (it) => (it.description ? it.description.slice(0, 90) + (it.description.length > 90 ? "…" : "") : null) },
    ],
    fields: [
      { key: "name", label: "Name / Event *", required: true, placeholder: "Person, organization, outing, or moment" },
      { key: "category", label: "Category", type: "select", options: ["Person", "Organization", "Outing", "Charity", "Course", "Partner", "Moment"] },
      { key: "description", label: "Short Description", type: "textarea", placeholder: "A sentence or two — what made this worth spotlighting" },
      { key: "photo", label: "Photo URL", placeholder: "/your-photo.jpg (send me the file) or https://…" },
      { key: "link", label: "Link (optional)", placeholder: "https://… — shown as a Learn More link" },
      { key: "status", label: "Status", type: "select", options: ["Draft", "Published"] },
    ],
    totals: (items) => [
      { label: "Published", value: items.filter((i) => i.status === "Published").length },
      { label: "Drafts", value: items.filter((i) => i.status !== "Published").length },
    ],
  },
};

const boardStatusStyle = (v) => {
  const map = {
    Published: "bg-brand-accent text-ink",
    Signed: "bg-brand-accent text-ink",
    Active: "bg-brand-accent text-ink",
    Delivered: "bg-brand-accent text-ink",
    Ready: "bg-brand text-white",
    Filmed: "bg-brand text-white",
    "In Progress": "bg-brand text-white",
    Planned: "bg-brand text-white",
    Negotiating: "bg-brand text-white",
    Contacted: "bg-brand text-white",
    Lost: "bg-red-500/20 text-red-400",
    Owed: "bg-red-500/20 text-red-400",
    Cancelled: "bg-red-500/20 text-red-400",
    Ended: "bg-white/5 text-white/40",
  };
  return map[v] || "bg-white/10 text-white/70";
};

const BoardTab = ({ kind, token, onAuthFail }) => {
  const cfg = BOARD_CONFIGS[kind];
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [dynOptions, setDynOptions] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };

  const loadItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/board/${kind}`, { headers });
      setItems(res.data.items);
    } catch (e) {
      if (e?.response?.status === 401) onAuthFail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, token]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    if (!cfg.dynamicOptions) return;
    axios
      .get(`${API}/admin/board/${cfg.dynamicOptions.sourceKind}`, { headers })
      .then((res) => setDynOptions(res.data.items.map((i) => i[cfg.dynamicOptions.valueKey]).filter(Boolean)))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, token]);

  const blankForm = () => {
    const f = {};
    cfg.fields.forEach((field) => {
      f[field.key] = field.type === "select" ? field.options[0] : "";
    });
    return f;
  };

  const openNew = () => {
    setForm(blankForm());
    setModal({ mode: "new" });
  };

  const openEdit = (item) => {
    const f = blankForm();
    cfg.fields.forEach((field) => {
      if (item[field.key] != null) f[field.key] = item[field.key];
    });
    setForm(f);
    setModal({ mode: "edit", item });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (modal.mode === "new") {
        await axios.post(`${API}/admin/board/${kind}`, { data: form }, { headers });
        toast.success("Added.");
      } else {
        await axios.patch(`${API}/admin/board/${kind}/${modal.item.id}`, { data: form }, { headers });
        toast.success("Updated.");
      }
      setModal(null);
      await loadItems();
    } catch (e2) {
      if (e2?.response?.status === 401) onAuthFail();
      else toast.error("Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const removeItem = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/board/${kind}/${modal.item.id}`, { headers });
      toast.success("Deleted.");
      setModal(null);
      await loadItems();
    } catch (e2) {
      if (e2?.response?.status === 401) onAuthFail();
      else toast.error("Could not delete.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8" data-testid={`board-${kind}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted" data-testid={`board-count-${kind}`}>
            {cfg.heading} · {items.length} item{items.length === 1 ? "" : "s"}
          </p>
          {cfg.totals && items.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid={`board-totals-${kind}`}>
              {cfg.totals(items).map((t) => (
                <span key={t.label} className="border border-white/10 bg-black/30 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-muted">
                  <span className="text-white/35">{t.label}:</span> <span className="text-white">{t.value}</span>
                </span>
              ))}
            </div>
          )}
        </div>
        <button onClick={openNew} data-testid={`board-new-${kind}`} className="flex items-center gap-2 bg-white px-5 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-brand-accent">
          <Plus className="h-3.5 w-3.5" />
          {cfg.newLabel}
        </button>
      </div>

      {items.length === 0 ? (
        <div className="mt-6 grid place-items-center gap-4 border border-dashed border-white/15 py-20 text-center">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/40">Nothing here yet — add your first one</p>
        </div>
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid={`board-grid-${kind}`}>
          {items.map((it) => (
            <button
              key={it.id}
              onClick={() => openEdit(it)}
              data-testid={`board-item-${it.id}`}
              className="border border-white/10 bg-ink-2 p-5 text-left transition-colors duration-300 hover:border-brand"
            >
              <div className="flex items-start justify-between gap-3">
                <h3 className="min-w-0 font-display text-lg font-extrabold uppercase leading-tight tracking-tight text-white">{it[cfg.titleKey]}</h3>
                {cfg.statusKey && it[cfg.statusKey] && (
                  <span className={`shrink-0 px-2.5 py-1 font-mono text-[9px] font-medium uppercase tracking-[0.15em] ${boardStatusStyle(it[cfg.statusKey])}`}>
                    {it[cfg.statusKey]}
                  </span>
                )}
              </div>
              <div className="mt-3 flex flex-col gap-1.5">
                {cfg.cardRows.map((row) => {
                  const v = row.render ? row.render(it) : it[row.key];
                  return v ? (
                    <p key={row.label} className="font-mono text-[11px] text-muted">
                      <span className="text-white/35">{row.label}:</span> {v}
                    </p>
                  ) : null;
                })}
              </div>
            </button>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-[95] grid place-items-center bg-black/70 p-5 backdrop-blur-sm" data-testid="board-modal" onClick={() => setModal(null)}>
          <form data-lenis-prevent onSubmit={save} onClick={(e) => e.stopPropagation()} className="max-h-[85vh] w-full max-w-lg overflow-y-auto overscroll-contain border border-white/15 bg-ink-2 p-7 md:p-9" data-testid="board-form">
            <div className="flex items-center justify-between">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand-accent">
                {modal.mode === "new" ? cfg.newLabel : "Edit"}
              </p>
              <button type="button" onClick={() => setModal(null)} className="text-white/50 hover:text-white" aria-label="Close" data-testid="board-modal-close">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-6 flex flex-col gap-4">
              {cfg.fields.map((field) => (
                <div key={field.key}>
                  <label htmlFor={`board-${field.key}`} className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-muted">{field.label}</label>
                  {cfg.dynamicOptions && field.key === cfg.dynamicOptions.field ? (
                    <select id={`board-${field.key}`} value={form[field.key] || ""} onChange={(e) => setForm((f) => ({ ...f, [field.key]: e.target.value }))} data-testid={`board-field-${field.key}`} className={`${inputCls} w-full appearance-none`}>
                      <option value="">— None —</option>
                      {dynOptions.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                      {form[field.key] && !dynOptions.includes(form[field.key]) && (
                        <option value={form[field.key]}>{form[field.key]} (not in Guest Research)</option>
                      )}
                    </select>
                  ) : field.type === "textarea" ? (
                    <textarea id={`board-${field.key}`} rows={3} value={form[field.key] || ""} onChange={(e) => setForm((f) => ({ ...f, [field.key]: e.target.value }))} placeholder={field.placeholder} data-testid={`board-field-${field.key}`} className={`${inputCls} w-full resize-none`} />
                  ) : field.type === "select" ? (
                    <select id={`board-${field.key}`} value={form[field.key] || field.options[0]} onChange={(e) => setForm((f) => ({ ...f, [field.key]: e.target.value }))} data-testid={`board-field-${field.key}`} className={`${inputCls} w-full appearance-none`}>
                      {field.options.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  ) : (
                    <input id={`board-${field.key}`} type={field.type || "text"} required={field.required} value={form[field.key] || ""} onChange={(e) => setForm((f) => ({ ...f, [field.key]: e.target.value }))} placeholder={field.placeholder} data-testid={`board-field-${field.key}`} className={`${inputCls} w-full ${field.type === "date" ? "[color-scheme:dark]" : ""}`} />
                  )}
                </div>
              ))}
            </div>

            <div className="mt-7 flex flex-wrap gap-3">
              <button type="submit" disabled={busy} data-testid="board-save" className="bg-brand-accent px-6 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-white disabled:opacity-60">
                {busy ? "Saving…" : modal.mode === "new" ? "Add" : "Save Changes"}
              </button>
              {modal.mode === "edit" && (
                <button type="button" onClick={removeItem} disabled={busy} data-testid="board-delete" className="border border-red-500/40 px-6 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-red-400 transition-colors duration-300 hover:bg-red-500/10 disabled:opacity-60">
                  Delete
                </button>
              )}
              <button type="button" onClick={() => setModal(null)} data-testid="board-cancel" className="border border-white/25 px-6 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-white/60">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};
